/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import classNames from 'classnames'
import { Dropdown, Text } from '@hashicorp/mds-react/components'
import { getTime } from 'app/[locale]/conferences/lib/utils'
import { type IRow } from 'app/[locale]/conferences/[conference]/agenda/[date]/page'
import AgendaCard from 'components/materia/conference-agenda/agenda-card'
import { vercelSplit } from '@web/cms'
import s from './style.module.css'

interface IAgendaPanel {
	currentSubtrack: string
	subtracks: string[]
	rows: IRow[]
	handleDropdownChange: (val: string) => void
	showDropdown?: boolean
}

const AgendaPanel = ({
	currentSubtrack,
	subtracks,
	rows,
	handleDropdownChange,
	showDropdown,
}: IAgendaPanel) => {
	return (
		<div className={s.root}>
			{showDropdown ? (
				<div className={s.dropdown}>
					<Dropdown.Root isFullWidth>
						<Dropdown.ToggleButton
							text={currentSubtrack}
							color="secondary"
							size="small"
							isFullWidth
						/>
						<Dropdown.Content width="100%">
							<Dropdown.List>
								{subtracks.map((subtrack) => {
									return (
										<Dropdown.ListItem.Interactive
											key={subtrack}
											text={subtrack}
											onClick={() => {
												handleDropdownChange(subtrack)
											}}
										/>
									)
								})}
							</Dropdown.List>
						</Dropdown.Content>
					</Dropdown.Root>
				</div>
			) : null}
			<div
				className={classNames([s.inner, { [s.hasNoPadding]: showDropdown }])}
			>
				{rows.map((row, rowId) => {
					return <AgendaRow row={row} subtracks={subtracks} key={rowId} />
				})}
			</div>
		</div>
	)
}

interface IAgendaRow {
	row: IRow
	subtracks: string[]
}
const getColumnFromSubtrack = (
	currentSubtrack: string,
	subtracks: string[],
) => {
	if (subtracks.includes(currentSubtrack)) {
		return subtracks.indexOf(currentSubtrack) + 1
	} else {
		return 0
	}
}

const AgendaRow = ({ row, subtracks }: IAgendaRow) => {
	// Do not render the subtrack headers on mobile. Subtracks are displayed via the
	// dropdown in the parent component.
	const isHiddenOnMobile = row.sessions.some((session) => {
		return session.__typename === 'ConferenceAgendaSubtrackSetRecord'
	})

	// Agenda items can be both sessions and non-sessions (line breaks, subtrack headers).
	// Both sessions and non-sessions have start times. Start times are used to put agenda items
	// in the correct chronological order. For non-sessions however, we do not want to show the
	// time to the user as it's irrelevant.
	const showsTime = row.sessions.some((session) => {
		const isSession =
			session.__typename === 'ConferenceGeneralSessionRecord' ||
			session.__typename === 'ConferenceSessionRecord'
		return isSession
	})
	const time = getTime(new Date(row.startTime))
	const columns = subtracks.length

	return (
		<div className={classNames([s.row, { [s.hidden]: isHiddenOnMobile }])}>
			{showsTime ? (
				<Text.Body tag="p" className={s.time} color="strong">
					{time}
				</Text.Body>
			) : (
				<div />
			)}
			<div
				className={s.rowItems}
				style={{
					['--columns' as string]: columns,
				}}
			>
				{row.sessions.map((session, sessionId) => {
					const isSession = session.__typename === 'ConferenceSessionRecord'
					let gridStart
					let gridEnd
					if (isSession) {
						gridStart = session?.confSubtrack?.order
							? getColumnFromSubtrack(
									vercelSplit(session?.confSubtrack?.name),
									subtracks,
								)
							: '1'
						gridEnd = session?.confSubtrack?.order ? 'span 1' : '-1'
					} else {
						gridStart = '1'
						gridEnd = '-1'
					}
					return (
						<div
							key={sessionId}
							className={s.rowItem}
							style={{
								['--gridStart' as string]: gridStart,
								['--gridEnd' as string]: gridEnd,
							}}
						>
							<AgendaItem row={session} />
						</div>
					)
				})}
			</div>
		</div>
	)
}

const AgendaItem = ({ row }) => {
	switch (row.__typename) {
		case 'ConferenceAgendaSubtrackSetRecord':
			return (
				<div className={s.agendaHeadersWrapper}>
					<AgendaHeaders headers={row.confSubtrack} />
				</div>
			)
		case 'ConferenceAgendaHorizontalRuleRecord':
			return <div className={s.divider} />
		case 'ConferenceSessionRecord':
			return <AgendaCard session={row} />
		case 'ConferenceGeneralSessionRecord':
			return <AgendaCard session={row} />
	}
}

interface IAgendaHeaders {
	headers: {
		name: string
	}[]
}

const AgendaHeaders = ({ headers }: IAgendaHeaders) => {
	return (
		<div
			className={s.agendaHeaders}
			style={{
				['--columns' as string]: headers.length,
			}}
		>
			{headers.map((header) => {
				return (
					<div key={header.name}>
						<Text.Body
							align="center"
							color="strong"
							tag="p"
							className={s.agendaHeader}
						>
							{vercelSplit(header.name)}
						</Text.Body>
					</div>
				)
			})}
		</div>
	)
}

export default AgendaPanel
